import React from "react";
import { ColletteChip } from "@collette-travel/collette-common-controls";
import { Rating, Stack, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import type { SearchParameters } from "@/types/dto/search-filters/search-parameters";
import { mapDisplayValueToApi, mapApiValueToDisplay, isRangeField } from "@/config/unified-search-config";
import searchParamKeys from "./search-param-keys";


interface AppliedFiltersProps {
  resultsCount: number;
  filters: Record<string, string[]>;
  handleDelete: (filter: Record<string, string>) => void;
  onFilterInteraction?: (filterType: string, filterValue: string | string[], action: "add" | "remove" | "clear") => void;
}

const AppliedFilters = ({
  resultsCount,
  filters,
  handleDelete,
  onFilterInteraction
}: AppliedFiltersProps) => {
  const t = useTranslations("SEARCH");

  const flattenedFiltersWithField = Object.entries(filters)
    .flatMap(([fieldName, values]) =>
      values.map(value => ({
        fieldName,
        value, // Keep original API value for deletion
        displayValue: mapApiValueToDisplay(fieldName, value) // ✅ Convert API value to display value
      }))
    )
    .map(filterItem => {
      // Special case for Price Range - it should be included even though the key is different
      if (filterItem.fieldName === "Price Range") {
        return { ...filterItem, fieldName: "PriceRange" };
      }
      return filterItem;
    })
    .filter(filterItem =>
      searchParamKeys.has(filterItem.fieldName as keyof SearchParameters)
    );

  return (
    <Stack
      sx={{
        flexWrap: "wrap",
        justifyContent: "flex-start",
        gap: "1.2rem",
        flexDirection: "row",
        alignItems: "center"
      }}
      data-testid="applied-filters"
    >
      <Typography data-testid="search-results-count">
        {t("Showing results for", {
          count: resultsCount,
          numFilters: flattenedFiltersWithField.length
        })}
      </Typography>
      {flattenedFiltersWithField.map((filterItem) => {
        const chipValue = filterItem.displayValue;

        // UNIFIED DELETION LOGIC - replaces all custom cases
        let deleteValue = filterItem.value;

        // For range fields, keep original format
        if (isRangeField(filterItem.fieldName)) {
          deleteValue = filterItem.value;
        }
        // For TourStyles, use the original API value that was stored
        else if (filterItem.fieldName === "TourStyles") {
          deleteValue = filterItem.value; // Keep the original API value (e.g., "Explorations", "Faith", "Cruising")
        }
        // For all other fields, use unified mapping to convert display value to API value
        else {
          deleteValue = mapDisplayValueToApi(filterItem.fieldName, filterItem.displayValue);
        }
        // Special handling for price range display format
        if (filterItem.fieldName === "PriceRange") {
          deleteValue = filterItem.value
            .replace(/\bFrom\b/g, "PriceFrom")
            .replace(/\bTo\b/g, "PriceTo")
            .replace(/\$/g, "");
        }

        if (filterItem.fieldName === "CustomerReview") {
          return (
            <ColletteChip
              key={`${filterItem.fieldName}-${filterItem.value}`}
              size="medium"
              label={
                <Rating
                  value={Number(filterItem.value)}
                  max={5}
                  precision={1}
                  readOnly
                  size="small"
                  data-testid={`applied-filter_${filterItem.displayValue}_star-rating`}
                  sx={{ mx: ".5rem" }}
                />
              }
              color="info"
              deleteIcon={<ClearOutlinedIcon color="primary" />}
              sx={{
                backgroundColor: "background.default",
                border: "1px solid",
                borderColor: "primary.main",
                borderRadius: "0.8rem",
                fontWeight: 600,
                "&.Mui-focusVisible": {
                  backgroundColor: "divider"
                }
              }}
              onDelete={() => {
                handleDelete({ [filterItem.fieldName]: filterItem.value });
                onFilterInteraction?.("Applied", `${filterItem.value} Stars`, "remove");
              }}
              testId={`applied-filter_${filterItem.displayValue}`}

            />
          );
        }
        return (
          <ColletteChip
            key={`${filterItem.fieldName}-${filterItem.value}`}
            size="medium"
            label={chipValue === "Small Group Explorations" ? "Small Group" : chipValue}
            color="info"
            deleteIcon={<ClearOutlinedIcon color="primary" />}
            sx={{
              backgroundColor: "background.default",
              border: "1px solid",
              borderColor: "primary.main",
              borderRadius: "0.8rem",
              fontSize: "1.4rem",
              fontWeight: 600,
              "&.Mui-focusVisible": {
                backgroundColor: "divider"
              }
            }}
            onDelete={() => {
              // Map PriceRange back to "Price Range" for handleDelete
              const fieldNameForDelete = filterItem.fieldName === "PriceRange" ? "Price Range" : filterItem.fieldName;
              handleDelete({ [fieldNameForDelete]: deleteValue });
              onFilterInteraction?.("Applied", filterItem.value, "remove");
            }}
            testId={`applied-filter_${filterItem.displayValue}`}
          />
        );

      })}
    </Stack>
  );
};

export default AppliedFilters;